import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { Link as RouterLink } from 'react-router-dom'
import { RemoteContent, Title, useAuth, useClubToday, useUnitSystem } from '@jupi/ui'
import { usePlanDraft } from './usePlanDraft'
import { useWorkedWeight } from './useWorkedWeight'
import { WorkedWeightSheet } from './WorkedWeightSheet'

// The weight & balance sum written out in full (#199), for checking the
// plan's figures by hand and for printing. Drawn from the same draft as the
// Weight page, with the same shared module, so it cannot disagree with it.
export default function WorkedWeightPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const system = useUnitSystem()
  const today = useClubToday()
  const editor = usePlanDraft()
  const { aircraft } = editor

  const weight = useWorkedWeight(editor)
  const { worked } = weight
  const performance = aircraft?.performance ?? null
  const sheet =
    worked && aircraft && performance ? (
      <WorkedWeightSheet
        worked={worked}
        envelope={performance.cgEnvelope}
        mtowKg={aircraft.mtowKg}
        registration={aircraft.registration}
        aircraftType={aircraft.aircraftType}
        pilotName={user?.displayName ?? ''}
        today={today}
        system={system}
      />
    ) : null

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Title label={t('plan.worked.title')} />
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Button component={RouterLink} to="/plan/weight" data-no-print startIcon={<Icon icon="mdi:arrow-left" />}>
            {t('plan.worked.back')}
          </Button>
          {sheet ? (
            <Button
              variant="contained"
              data-no-print
              startIcon={<Icon icon="mdi:printer-outline" />}
              onClick={() => window.print()}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              {t('plan.ofp.print')}
            </Button>
          ) : null}
        </Stack>
      </Stack>
      <RemoteContent isLoading={editor.isLoading} error={editor.error}>
        {sheet ? (
          // The sheet is paper-sized; on a phone it scrolls rather than reflows.
          <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}>{sheet}</Box>
        ) : (
          <Alert severity="info">{t('plan.weight.noPerformance')}</Alert>
        )}
      </RemoteContent>
      {/* What Print prints: beside the app, which is hidden for it. */}
      {sheet ? createPortal(<div className="wb-print">{sheet}</div>, document.body) : null}
    </Box>
  )
}
